import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getProductBySlug } from '../../api/product';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { setSeoMeta, toAbsoluteUrl, upsertJsonLd, removeJsonLd } from '../../utils/seo';
import './ProductDetail.css';

const fmt = n => (Number(n || 0)).toLocaleString('ru-RU');

function isVideoMedia(im) {
  if (im?.type === 'video') return true;
  const url = String(im?.url || '');
  return /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url);
}

function Lightbox({ images, index, onClose, onChange }) {
  const current = images[index];
  const isVideo = isVideoMedia(current);
  const multi = images.length > 1;

  const go = useCallback((d) => {
    if (!multi) return;
    onChange((index + d + images.length) % images.length);
  }, [index, images.length, multi, onChange]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [go, onClose]);

  if (!current) return null;

  return (
    <div className="pd-lb" role="dialog" aria-modal="true" aria-label="Просмотр медиа" onClick={onClose}>
      <div className="pd-lb__shell" onClick={(e) => e.stopPropagation()}>
        <div className={`pd-lb__frame ${multi ? '' : 'pd-lb__frame--solo'}`.trim()}>
          {multi && (
            <button
              type="button"
              className="pd-lb__nav"
              onClick={() => go(-1)}
              aria-label="Назад"
            >
              ‹
            </button>
          )}

          <div className="pd-lb__stage">
            <button
              type="button"
              className="pd-lb__close"
              onClick={onClose}
              aria-label="Закрыть"
            >
              ×
            </button>

            {isVideo ? (
              <video
                key={current.url}
                src={current.url}
                controls
                autoPlay
                playsInline
                className="pd-lb__media"
              />
            ) : (
              <img
                src={current.url}
                alt={current.alt || ''}
                className="pd-lb__media"
              />
            )}
          </div>

          {multi && (
            <button
              type="button"
              className="pd-lb__nav"
              onClick={() => go(1)}
              aria-label="Вперёд"
            >
              ›
            </button>
          )}
        </div>

        {multi && (
          <div className="pd-lb__counter">
            {index + 1} / {images.length}
          </div>
        )}
      </div>
    </div>
  );
}

function Gallery({ images = [], title = '' }) {
  const [i, setI] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const touchX = useRef(null);

  useEffect(() => {
    setI(0);
  }, [images]);

  useEffect(() => {
    const next = images[i + 1], prev = images[i - 1];
    [next, prev].forEach((im) => {
      if (!im?.url || isVideoMedia(im)) return;
      const img = new Image();
      img.src = im.url;
    });
  }, [i, images]);

  if (!images.length) {
    return (
      <div className="gall">
        <div className="gall__stage gall__stage--empty" aria-label="Нет изображения" />
      </div>
    );
  }

  const multi = images.length > 1;
  const go = (d) => setI(p => (p + d + images.length) % images.length);
  const current = images[i];
  const currentIsVideo = isVideoMedia(current);

  const onTouchStart = (e) => {
    if (!multi) return;
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current == null || !multi) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  const openLightbox = () => {
    if (currentIsVideo) return;
    setLightbox(true);
  };

  return (
    <div className={`gall ${multi ? '' : 'gall--solo'}`.trim()}>
      <div className={`gall__grid ${multi ? '' : 'gall__grid--solo'}`.trim()}>
        {multi && (
          <div className="gall__thumbs" role="tablist" aria-label="Миниатюры">
            {images.map((im, idx) => (
              <button
                key={im.url + idx}
                type="button"
                role="tab"
                aria-selected={idx === i}
                className={`gall__thumb ${idx === i ? 'is-active' : ''}`}
                onClick={() => setI(idx)}
                aria-label={`Показать медиа ${idx + 1}`}
              >
                {isVideoMedia(im) ? (
                  <video src={im.url} muted playsInline preload="metadata" />
                ) : (
                  <img src={im.url} alt={im.alt || `${title} — фото ${idx + 1}`} />
                )}
              </button>
            ))}
          </div>
        )}

        <div
          className="gall__stage"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {currentIsVideo ? (
            <video
              key={current.url}
              src={current.url}
              controls
              playsInline
              preload="metadata"
            />
          ) : (
            <button
              type="button"
              className="gall__zoom"
              onClick={openLightbox}
              aria-label="Открыть фото крупно"
            >
              <img src={current.url} alt={current.alt || title} />
              <span className="gall__zoom-badge" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
                  <path d="M16.5 16.5L21 21M11 8v6M8 11h6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
            </button>
          )}

          {multi && (
            <>
              <button type="button" className="gall__nav gall__prev" onClick={() => go(-1)} aria-label="Назад">‹</button>
              <button type="button" className="gall__nav gall__next" onClick={() => go(1)} aria-label="Вперёд">›</button>
              <div className="gall__counter">{i + 1} / {images.length}</div>
            </>
          )}
        </div>
      </div>

      {multi && (
        <div className="gall__thumbs gall__thumbs--mobile">
          {images.map((im, idx) => (
            <button
              key={'m' + im.url + idx}
              type="button"
              className={`gall__thumb ${idx === i ? 'is-active' : ''}`}
              onClick={() => setI(idx)}
              aria-label={`Показать медиа ${idx + 1}`}
            >
              {isVideoMedia(im) ? (
                <video src={im.url} muted playsInline preload="metadata" />
              ) : (
                <img src={im.url} alt={im.alt || ''} />
              )}
            </button>
          ))}
        </div>
      )}

      {lightbox && (
        <Lightbox
          images={images}
          index={i}
          onClose={() => setLightbox(false)}
          onChange={setI}
        />
      )}
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const role = user?.role || 'guest';
  const { add } = useCart();

  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const data = await getProductBySlug(slug);
        if (!mounted) return;
        setP(data);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [slug]);

  const availableQty = useMemo(
    () => Math.max(0, Number(p?.availableQty ?? (Number(p?.stockQty || 0) - Number(p?.reservedQty || 0)))),
    [p]
  );
  const minQty = availableQty > 0 ? 1 : 0;

  useEffect(() => {
    setQty(q => Math.min(Math.max(minQty, q), Math.max(minQty, availableQty)));
  }, [availableQty, minQty]);

  const priceView = useMemo(() => {
    if (!p) return null;
    const raw = p.price || {};
    const toNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const retail = toNum(raw.retail ?? p.priceRetail);
    const wholesale = toNum(
      raw.wholesale ?? (['admin', 'manager'].includes(role) ? p.priceWholesale : null)
    );
    const effective = toNum(raw.effective) ?? retail;
    return { retail, effective, wholesale };
  }, [p, role]);

  // для опта / admin / manager: розница и опт с подписями
  const priceDisplay = useMemo(() => {
    if (!priceView) return null;
    const privileged = ['wholesale', 'admin', 'manager'].includes(role);
    const { retail, wholesale, effective } = priceView;

    if (privileged && wholesale != null) {
      const opt = role === 'wholesale' ? (effective ?? wholesale) : wholesale;
      return {
        mode: 'wholesale',
        retail,
        wholesale: opt,
      };
    }
    return { mode: 'retail', retail: effective, wholesale: null };
  }, [priceView, role]);

  useEffect(() => {
    if (!p) return;

    const canonical = toAbsoluteUrl(`/p/${slug}`);
    const title = `${p.title} — купить в LavSan`;
    const description = p.shortDesc || p.fullDesc || `${p.title}. Цена, наличие и характеристики в интернет-магазине LavSan.`;
    const firstImage = p.images?.[0]?.url ? toAbsoluteUrl(p.images[0].url) : undefined;

    setSeoMeta({
      title,
      description: String(description).slice(0, 220),
      canonical,
      og: {
        type: 'product',
        title,
        description: String(description).slice(0, 220),
        url: canonical,
        image: firstImage,
      },
    });

    const amount = typeof priceView?.effective === 'number'
      ? priceView.effective
      : (typeof priceView?.retail === 'number' ? priceView.retail : undefined);

    upsertJsonLd('product-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: p.title,
      image: (p.images || []).map(im => toAbsoluteUrl(im.url)).slice(0, 6),
      sku: p.sku || undefined,
      brand: p.brand ? { '@type': 'Brand', name: p.brand } : undefined,
      description: String(description).slice(0, 500),
      offers: amount ? {
        '@type': 'Offer',
        priceCurrency: 'RUB',
        price: amount,
        availability: availableQty > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        url: canonical,
      } : undefined,
    });

    const breadcrumbItems = [
      { '@type': 'ListItem', position: 1, name: 'Главная', item: toAbsoluteUrl('/') },
      { '@type': 'ListItem', position: 2, name: 'Каталог', item: toAbsoluteUrl('/catalog') },
    ];

    if (p.topCategorySlug) {
      breadcrumbItems.push({
        '@type': 'ListItem',
        position: 3,
        name: p.topCategoryName?.ru || p.topCategoryName || p.topCategorySlug,
        item: toAbsoluteUrl(`/catalog/${p.topCategorySlug}`),
      });
    }

    breadcrumbItems.push({
      '@type': 'ListItem',
      position: breadcrumbItems.length + 1,
      name: p.title,
      item: canonical,
    });

    upsertJsonLd('breadcrumb-product', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems,
    });

    return () => {
      removeJsonLd('product-jsonld');
      removeJsonLd('breadcrumb-product');
    };
  }, [p, slug, priceView, availableQty]);

  const specs = useMemo(() => {
    const arr = [];
    if (p?.sku) arr.push(['Артикул', p.sku]);
    if (p?.brand) arr.push(['Бренд', p.brand]);
    if (p?.color) arr.push(['Цвет', p.color]);
    if (p?.country) arr.push(['Страна', p.country]);
    if (Number(p?.warrantyMonths) > 0) arr.push(['Гарантия', `${p.warrantyMonths} мес.`]);
    if (p?.weightKg) arr.push(['Вес', `${p.weightKg} кг`]);
    const d = p?.dimensions || {};
    if (d.lengthMm || d.widthMm || d.heightMm) {
      const triplet = [d.lengthMm, d.widthMm, d.heightMm].filter(Boolean).join(' × ');
      if (triplet) arr.push(['Габариты', `${triplet} мм`]);
    }

    const features = Array.isArray(p?.features) ? p.features : [];
    const byCode = new Map(features.map((f) => [f.code, f]));
    const attrs = p?.attributes && typeof p.attributes === 'object' ? p.attributes : {};

    const formatAttr = (code, val) => {
      if (val === '' || val === null || val === undefined) return null;
      const f = byCode.get(code);
      const name = f?.name?.ru || code;
      if (f?.type === 'bool' || typeof val === 'boolean') {
        return [name, val ? 'Да' : 'Нет'];
      }
      if (f?.type === 'enum') {
        const opt = (f.values || []).find((v) => String(v.code ?? v) === String(val));
        const label = opt?.label?.ru ?? opt?.label ?? val;
        return [name, f.unit ? `${label} ${f.unit}` : String(label)];
      }
      if (f?.type === 'number' || typeof val === 'number') {
        return [name, f?.unit ? `${val} ${f.unit}` : String(val)];
      }
      return [name, String(val)];
    };

    const orderedCodes = features.map((f) => f.code);
    const extraCodes = Object.keys(attrs).filter((c) => !orderedCodes.includes(c));
    for (const code of [...orderedCodes, ...extraCodes]) {
      const row = formatAttr(code, attrs[code]);
      if (row) arr.push(row);
    }
    return arr;
  }, [p]);

  if (loading) {
    return (
      <div className="page container pd">
        <div className="pd__skeleton" aria-busy="true">Загрузка…</div>
      </div>
    );
  }
  if (!p) {
    return (
      <div className="page container pd">
        <div className="pd__empty">
          <p>Товар не найден</p>
          <Link to="/catalog">В каталог</Link>
        </div>
      </div>
    );
  }

  const availability = p.availability?.label || (availableQty > 0 ? 'В наличии' : 'Нет в наличии');
  const catName = p.topCategoryName?.ru || p.topCategoryName || p.topCategorySlug;

  return (
    <div className="page container pd">
      <nav className="pd__crumbs" aria-label="Хлебные крошки">
        <Link to="/">Главная</Link>
        <span>/</span>
        <Link to="/catalog">Каталог</Link>
        {p.topCategorySlug && (
          <>
            <span>/</span>
            <Link to={`/catalog/${p.topCategorySlug}`}>{catName}</Link>
          </>
        )}
        <span>/</span>
        <span className="pd__crumb-current">{p.title}</span>
      </nav>

      <div className="pd__grid">
        <Gallery images={p.images} title={p.title} />

        <aside className="pd__aside">
          <div className="pd__head">
            {p.brand && <div className="pd__brand">{p.brand}</div>}
            <h1 className="pd__title">{p.title}</h1>
            {p.sku && <div className="pd__sku">Арт. {p.sku}</div>}
          </div>

          {priceDisplay && (
            <div className="pd__price">
              {priceDisplay.mode === 'wholesale' ? (
                <>
                  {priceDisplay.retail != null && (
                    <div className="pd__price-row">
                      <span className="pd__price-label">Розничная цена</span>
                      <span className={`pd__price-value ${priceDisplay.retail > priceDisplay.wholesale ? 'is-old' : ''}`}>
                        {fmt(priceDisplay.retail)} ₽
                      </span>
                    </div>
                  )}
                  <div className="pd__price-row pd__price-row--main">
                    <span className="pd__price-label">Оптовая цена</span>
                    <span className="pd__price-value">
                      <b>{fmt(priceDisplay.wholesale)} ₽</b>
                    </span>
                  </div>
                </>
              ) : (
                <div className="pd__now">
                  <b>{fmt(priceDisplay.retail)} ₽</b>
                </div>
              )}
            </div>
          )}

          <div className={`pd__avail ${availableQty > 0 ? 'pd__avail--in' : 'pd__avail--out'}`}>
            {availability}
          </div>

          <div className="pd__buy">
            <div className="pd__qty" role="group" aria-label="Количество">
              <button type="button" onClick={() => setQty(q => Math.max(minQty, q - 1))} aria-label="Минус">−</button>
              <output className="pd__qtyv" aria-live="polite">{qty}</output>
              <button
                type="button"
                onClick={() => setQty(q => Math.min(Math.max(minQty, availableQty), q + 1))}
                aria-label="Плюс"
              >
                +
              </button>
            </div>
            <button
              type="button"
              className="pd__cart"
              onClick={() => add(p, qty)}
              disabled={availableQty <= 0}
            >
              {availableQty > 0 ? 'В корзину' : 'Нет в наличии'}
            </button>
          </div>

          {p.shortDesc && <p className="pd__short">{p.shortDesc}</p>}
        </aside>
      </div>

      {(p.fullDesc || specs.length > 0) && (
        <div className="pd__below">
          {p.fullDesc && (
            <section className="pd__card">
              <h2>Описание</h2>
              <div className="pd__desc">{p.fullDesc}</div>
            </section>
          )}

          {specs.length > 0 && (
            <section className="pd__card pd__card--specs">
              <h2>Характеристики</h2>
              <dl className="pd__specs">
                {specs.map(([k, v], idx) => (
                  <div className="pd__spec" key={idx}>
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
